import React, {
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  createStyles,
  Divider,
  Grid,
  Link,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import { CustomDialog } from '../common/custom-dialog';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { toSentenceCase } from '../../../../util/format';
import SearchAccount from '../common/search-account/SearchAccount';
import Tabs from '../../../tabs';
import { AccountWithOutageInfo } from '../../types';
import clsx from 'clsx';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import ReportOutageIcon from '../icons/ReportOutageIcon';
import { useReportOutage } from '../../hooks/useReportOutage';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import useOutageCenterUtils from '../../../outage/outage-center/utils';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import OutageStatusGreen from '../../../../static/outageIconsV2/outage_status_green.svg';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      borderRadius: 0,
      padding: 5,
    },
    accountLink: {
      fontSize: '1rem',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    accountData: {},
    selectedAccount: {
      backgroundColor: colors.lightYellow,
    },
    dot: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: colors.roseRed,
      display: 'inline-block',
      marginRight: 10,
    },
    checkbox: {
      '&.MuiIconButton-root': {
        padding: 0,
      },
    },
    footerContainer: {
      marginTop: theme.spacing(2),
      flexDirection: 'row',
      alignItems: 'center',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
        alignItems: 'normal',
        gap: '20px',
      },
    },
    close: {
      textAlign: 'center',
    },
    reportOutageButton: {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
      width: '100%',
      padding: theme.spacing(1.5, 0),
      borderRadius: '40px',
      color: colors.white,
      fontSize: '1rem',
      fontWeight: 600,
      '&.Mui-disabled': {
        backgroundColor: colors.grey3,
        color: colors.cloudGrey2,
      },
      '&:hover': {
        backgroundColor: colors.orange,
        borderColor: colors.orange,
      },
      '& svg': {
        marginRight: '8px',
      },
    },
    confirmationContainer: {
      background: colors.white,
      padding: theme.spacing(3.75, 2.5),
    },
    confirmationTitle: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    locationData: {
      fontSize: '1rem',
    },
    boldText: {
      fontWeight: 600,
    },
  }),
);

interface ViewAllAccountsDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  accountListWithOutageInfo: AccountWithOutageInfo[];
  refetchAccountsOutageInfo: () => void;
}
const MAX_SELECTION = 5;
export const ViewAllAccountsDialog = ({
  open,
  setOpen,
  accountListWithOutageInfo,
  refetchAccountsOutageInfo,
}: ViewAllAccountsDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    selectedAccountForOutage,
    setSelectedAccountForOutage,
  } = useOutageLeftPanelState();
  const [searchAccountQuery, setSearchAccountQuery] = useState<string>('');
  const [selectedAccounts, setSelectedAccounts] = useState<
    AccountWithOutageInfo[]
  >([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const { setState: updateTransition } = useContext(TransitionContext);
  const { reportOutageForLoggedInUser } = useReportOutage();
  const { setErrorNotification, getRequestSource } = useOutageCenterUtils();
  const { wrapWithLoader } = useWrapWithLoader();
  const [outageReported, setOutageReported] = useState<boolean>(false);

  const handleClose = () => {
    setOpen(false);
    setOutageReported(false);
    setSelectedAccounts([]);
    if (outageReported) {
      refetchAccountsOutageInfo();
    }
  };

  const closeAndShowError = () => {
    setErrorNotification(true);
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const handleSelectAccount = (account: AccountWithOutageInfo) => {
    setSelectedAccountForOutage(account);
    setOpen(false);
    setSearchAccountQuery('');
  };

  const handleSelectAccountCheckbox = (
    account: AccountWithOutageInfo,
    checked: boolean,
  ) => {
    if (!account.accountNumber) {
      return;
    }

    setSelectedAccounts(prev => {
      if (checked) {
        if (prev.length >= MAX_SELECTION) {
          return prev;
        }
        return prev.some(a => a.accountNumber === account.accountNumber)
          ? prev
          : [...prev, account];
      } else {
        return prev.filter(a => a.accountNumber !== account.accountNumber);
      }
    });
  };

  const handleSearch = useCallback((query: string) => {
    setSearchAccountQuery(query.trim().toLowerCase());
  }, []);

  const filteredAccounts = useMemo(() => {
    if (!searchAccountQuery) {
      return accountListWithOutageInfo;
    }
    return accountListWithOutageInfo.filter(a =>
      a.accountNumber?.toLowerCase().includes(searchAccountQuery),
    );
  }, [searchAccountQuery, accountListWithOutageInfo]);

  const accountsWithOutages = useMemo(
    () => filteredAccounts.filter(a => a.hasKnownOutage),
    [filteredAccounts],
  );
  const accountsWithNoOutages = useMemo(
    () => filteredAccounts.filter(a => !a.hasKnownOutage),
    [filteredAccounts],
  );

  const handleReportOutage = wrapWithLoader(async () => {
    try {
      const promises = selectedAccounts.map(account => {
        const request = {
          accountNumber: account.accountNumber!,
          encryptedPersonId: account.encryptedPersonId!,
          encryptedPremiseId: account.premiseInfos?.[0].encryptedPremiseId!,
          encryptedServicePointList:
            account.premiseInfos?.[0].servicePointDetails?.map(
              sp => sp!.encryptedServicePointId,
            ) ?? [],
          source: getRequestSource(),
        };

        return reportOutageForLoggedInUser(request);
      });

      const results = await Promise.all(promises);

      const allSucceeded = results.every(
        res => res?.reportOutageBySpList?.success,
      );

      if (allSucceeded) {
        console.log('Report outage success for all accounts');
        setOutageReported(true);
      } else {
        console.warn('Some outage reports failed', results);
        closeAndShowError();
      }

      updateTransition({ open: false });
    } catch (error) {
      console.error('Error reporting outage', error);
      closeAndShowError();
      updateTransition({ open: false });
    }
  });

  return (
    <Fragment>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={toSentenceCase(t('ALL_ACCOUNTS'))}
        titleBgColor={colors.riverBlue}
        titleTextColor={colors.noirBlur}
        dialogWidth={800}
      >
        {outageReported ? (
          <ReportOutageConfirmation filterAccounts={selectedAccounts} />
        ) : (
          <Box px={3.75} pb={3.75}>
            <Box mb={1}>
              <SearchAccount onSearch={handleSearch} />
            </Box>

            <Box>
              <Paper className={classes.paper}>
                {accountsWithOutages.length > 0 ? (
                  <Tabs
                    selectedIndex={selectedTabIndex}
                    onChange={setSelectedTabIndex}
                    tabHeight="60px"
                    noTabPanelPadding={true}
                    tabs={[
                      {
                        label: `${accountsWithOutages.length} ${t('OUTAGES')}`,
                        icon: (
                          <span
                            className={classes.dot}
                            aria-hidden="true"
                          ></span>
                        ),
                        data: (
                          <>
                            {accountsWithOutages.map(account => (
                              <AccountItem
                                key={account.accountNumber}
                                account={account}
                                maxAccountsSelected={
                                  selectedAccounts.length >= MAX_SELECTION
                                }
                                selectedAcc={
                                  selectedAccountForOutage?.accountNumber ===
                                  account.accountNumber
                                }
                                selected={selectedAccounts.some(
                                  acc =>
                                    acc.accountNumber === account.accountNumber,
                                )}
                                onSelect={handleSelectAccountCheckbox}
                                onSelectAcc={handleSelectAccount}
                              />
                            ))}
                          </>
                        ),
                      },
                      {
                        label: t('NO_OUTAGES'),
                        data: (
                          <Box>
                            {accountsWithNoOutages.length === 0 && (
                              <Box p={2.5}>
                                <Typography variant="body1">
                                  {t('NO_ACCOUNTS_FOUND')}
                                </Typography>
                              </Box>
                            )}
                            {accountsWithNoOutages.length > 5 && (
                              <Box px={2} pt={2.5}>
                                <Typography variant="body1">
                                  {t('NOTE_SELECT_UPTO_FIVE_ACCOUNTS')}
                                </Typography>
                              </Box>
                            )}
                            {accountsWithNoOutages.map(account => (
                              <AccountItem
                                key={account.accountNumber}
                                account={account}
                                maxAccountsSelected={
                                  selectedAccounts.length >= MAX_SELECTION
                                }
                                selectedAcc={
                                  selectedAccountForOutage?.accountNumber ===
                                  account.accountNumber
                                }
                                selected={selectedAccounts.some(
                                  acc =>
                                    acc.accountNumber === account.accountNumber,
                                )}
                                onSelect={handleSelectAccountCheckbox}
                                onSelectAcc={handleSelectAccount}
                              />
                            ))}
                          </Box>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Box width={'inherit'}>
                    {filteredAccounts.length > 5 && (
                      <Box px={2} pt={2.5}>
                        <Typography variant="body1">
                          {t('NOTE_SELECT_UPTO_FIVE_ACCOUNTS')}
                        </Typography>
                      </Box>
                    )}
                    {filteredAccounts.map(account => (
                      <AccountItem
                        key={account.accountNumber}
                        account={account}
                        maxAccountsSelected={
                          selectedAccounts.length >= MAX_SELECTION
                        }
                        selectedAcc={
                          selectedAccountForOutage?.accountNumber ===
                          account.accountNumber
                        }
                        selected={selectedAccounts.some(
                          acc => acc.accountNumber === account.accountNumber,
                        )}
                        onSelect={handleSelectAccountCheckbox}
                        onSelectAcc={handleSelectAccount}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
            {(selectedTabIndex === 1 || accountsWithOutages.length === 0) && (
              <Grid
                container
                justify="space-around"
                className={classes.footerContainer}
              >
                <Grid item xs={12} md={5} className={classes.close}>
                  <Link onClick={() => setOpen(false)}>{t('CLOSE')}</Link>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    className={classes.reportOutageButton}
                    onClick={() => handleReportOutage()}
                    disabled={selectedAccounts.length === 0}
                  >
                    <ReportOutageIcon
                      size={24}
                      color={
                        selectedAccounts.length === 0
                          ? colors.grey2
                          : colors.white
                      }
                      variant="filled"
                    />
                    {t('REPORT_OUTAGES')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </CustomDialog>
    </Fragment>
  );
};

const AccountItem: React.FC<{
  account: AccountWithOutageInfo;
  selected: boolean;
  selectedAcc: boolean;
  maxAccountsSelected: boolean;
  onSelectAcc: (account: AccountWithOutageInfo) => void;
  onSelect: (account: AccountWithOutageInfo, checked: boolean) => void;
}> = ({
  account,
  selected,
  selectedAcc,
  maxAccountsSelected,
  onSelect,
  onSelectAcc,
}) => {
  const classes = useStyles();
  return (
    <Fragment key={account.accountNumber}>
      <Box
        p={2.5}
        display="flex"
        gridGap={20}
        alignItems={'center'}
        className={clsx(classes.accountData, {
          [classes.selectedAccount]: selectedAcc,
        })}
      >
        {!account.hasKnownOutage && (
          <Checkbox
            color="primary"
            checked={selected}
            onChange={e => onSelect(account, e.target.checked)}
            onClick={e => e.stopPropagation()}
            inputProps={{
              'aria-label': `Select account ${account.accountNumber}`,
            }}
            disabled={maxAccountsSelected && !selected}
            className={classes.checkbox}
          />
        )}

        <Box display="flex" flexDirection="column" gridGap={10}>
          <Link
            className={classes.accountLink}
            onClick={() => onSelectAcc(account)}
          >
            {account.accountNumber}
          </Link>
          <Typography variant="body2">{account.addressLocation}</Typography>
        </Box>
      </Box>
      <Divider />
    </Fragment>
  );
};

const ReportOutageConfirmation: React.FC<{
  filterAccounts: AccountWithOutageInfo[];
}> = ({ filterAccounts }: { filterAccounts: AccountWithOutageInfo[] }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Fragment>
      <Box px={3.75} pb={3.75}>
        <Box className={classes.confirmationContainer}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            gridGap={20}
            alignItems={'center'}
          >
            <OutageStatusGreen width={57} height={56} />
            <Typography variant="body1" className={classes.confirmationTitle}>
              {t('YOUR_OUTAGE_HAS_BEEN_REPORTED')}
            </Typography>
            <Box
              pt={2.5}
              display={'flex'}
              flexDirection={'column'}
              gridGap={20}
            >
              <Typography variant="body1" className={classes.boldText}>
                {t('THANK_YOU_FOR_REPORTING_OUTAGE')}
              </Typography>
              <Typography variant="body1">
                {t('NOTE_FOR_REPORTED_OUTAGES')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className={classes.confirmationContainer}>
          <Box display={'flex'} flexDirection={'column'} gridGap={20}>
            <Typography
              variant="body1"
              className={clsx(classes.locationData, classes.boldText)}
            >
              {t('OUTAGE_LOCATIONS')}
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gridGap={10}>
              {filterAccounts.map(acc => {
                const premise = acc.premiseInfos?.[0];
                return (
                  <Box display={'flex'} flexDirection={'column'} gridGap={10}>
                    <Typography
                      variant="body1"
                      className={clsx(classes.locationData)}
                    >
                      {acc.accountNumber}
                    </Typography>
                    <Box display={'flex'} flexDirection={'column'}>
                      <Typography
                        variant="body1"
                        className={clsx(classes.locationData, classes.boldText)}
                      >
                        {premise?.addressLine1} {premise?.addressLine2}
                      </Typography>
                      <Typography
                        variant="body1"
                        className={clsx(classes.locationData, classes.boldText)}
                      >
                        {premise?.city} {premise?.state} {premise?.postal}
                      </Typography>
                    </Box>
                    <Divider />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};
